/**
 * What a pathways map shows: each player's Krall's Accord route through a
 * round as trails on the round's planet map, where they were at a moment of
 * the round, and the list of players beside it.
 *
 * Pure: the paths come from the server once the round has ended. Points from
 * another area are left off (planets' map bounds overlap), and a stretch with
 * no ticks for a while (Krall's Accord taken off, or a lost connection) breaks
 * the line rather than drawing a straight jump across the map.
 */
import { getPlayerDisplayName, isHideSeekRoundPlayer, isHnstRoundPlayer } from '../types/player';
import { worldToMapPercent } from './mapGeometry';
/** A gap this long between ticks breaks a path's line. */
export const PATH_GAP_MS = 30000;
/** The marker id of a player's position on the pathways map. */
export function pathMarkerId(playerId) {
    return `path:${playerId}`;
}
/** Whether a point belongs on the round's maps. */
function inRoundArea(point, location) {
    return point.areaId === undefined || point.areaId === String(location.logAreaId);
}
/** A path's points split where ticks stopped for a while. */
export function splitPathAtGaps(points, gapMs = PATH_GAP_MS) {
    const segments = [];
    let current = [];
    for (const point of points) {
        const last = current[current.length - 1];
        if (last && point.at - last.at > gapMs) {
            segments.push(current);
            current = [];
        }
        current.push(point);
    }
    if (current.length > 0)
        segments.push(current);
    return segments;
}
export function buildHnsPathView(input) {
    const { round, location, map, paths, players, viewerId = null, until = null, hidden, selectedPlayerId = null } = input;
    const toneAndRole = (playerId) => {
        const roundPlayer = round.players.find((p) => p.playerId === playerId);
        const role = roundPlayer && isHideSeekRoundPlayer(roundPlayer) ? roundPlayer.originalRole ?? roundPlayer.role : undefined;
        const team = roundPlayer && isHnstRoundPlayer(roundPlayer) ? roundPlayer.team : undefined;
        const tone = playerId === viewerId ? 'self' : team ?? role ?? 'target';
        return { tone, ...(team && { team }), ...(role && { role }) };
    };
    const pathOf = new Map(paths.map((path) => [path.playerId, path.points.filter((p) => inRoundArea(p, location))]));
    const ids = [...round.players.map((p) => p.playerId), ...paths.map((p) => p.playerId).filter((id) => !round.players.some((rp) => rp.playerId === id))];
    const rows = ids.map((playerId) => ({
        playerId,
        name: getPlayerDisplayName(playerId, players),
        ...toneAndRole(playerId),
        hasPath: (pathOf.get(playerId)?.length ?? 0) > 0,
    }));
    const trails = [];
    const markers = [];
    const place = (p) => worldToMapPercent(map.bounds, p.x, p.y);
    for (const row of rows) {
        if (hidden?.has(row.playerId))
            continue;
        const shown = (pathOf.get(row.playerId) ?? []).filter((p) => until === null || p.at <= until);
        if (shown.length === 0)
            continue;
        const faded = selectedPlayerId !== null && selectedPlayerId !== row.playerId;
        const selected = selectedPlayerId === row.playerId;
        splitPathAtGaps(shown).forEach((segment, index) => {
            const points = segment.map(place).filter((p) => p !== null);
            if (points.length > 1) {
                trails.push({
                    id: `path:${row.playerId}:${index}`, tone: row.tone, points,
                    ...(faded && { faded: true }), ...(selected && { selected: true }),
                });
            }
        });
        const first = place(shown[0]);
        if (first && shown.length > 1) {
            markers.push({ id: `start:${row.playerId}`, kind: 'dot', tone: row.tone, ...first, stale: true });
        }
        const last = place(shown[shown.length - 1]);
        if (last) {
            markers.push({ id: pathMarkerId(row.playerId), kind: 'dot', tone: row.tone, ...last, label: row.name, ...(faded && { stale: true }) });
        }
    }
    const times = [...pathOf.values()].flat().map((p) => p.at);
    const startAt = round.startedAt ?? (times.length ? Math.min(...times) : null);
    const endAt = round.endedAt ?? (times.length ? Math.max(...times) : null);
    return { trails, markers, rows, startAt, endAt };
}
